import {NextRouter} from "next/router";
import {Item} from "../components/common/CustomListbox";
import {ParsedUrlQuery} from "querystring";
import {Filter, FilterAnyLabels, PropertyTypeQueryKey} from "../data/Filter/Filter.config";
import {VermietungCategories} from "../data/Filter/VermietungFilter.config";
import {Distances, NumberOfRooms} from "../enums/enums";
import {CustomTFunction, TranslationsKeys} from "../TranslationTypes";

export function replaceRouterQueryParamsShallow(
	router: NextRouter,
	queryParams?: ParsedUrlQuery,
	pageChanged?: boolean
) {
	let newQueryParams = queryParams;
	if (!pageChanged && newQueryParams) {
		delete newQueryParams.page;
	}
	router.replace(
		{
			query: newQueryParams
		},
		undefined,
		{
			shallow: true
		}
	);
}

export function handleSearchChange(searchVal: string, router: NextRouter) {
	const {q, ...queryParams} = router.query;
	if (searchVal && searchVal !== "") {
		queryParams.q = searchVal;
	}
	queryParams.page = "1";
	replaceRouterQueryParamsShallow(router, queryParams);
}

export function handleIDSearchChange(searchVal: string, router: NextRouter) {
	const {id, ...queryParams} = router.query;
	if (searchVal && searchVal !== "") {
		queryParams.id = searchVal;
	}
	queryParams.page = "1";
	replaceRouterQueryParamsShallow(router, queryParams);
}

export function getQueryParamByLabel(key: string) {
	let searchKey = "";
	switch (key) {
		case Filter.BathroomsTotal.label:
			searchKey = Filter.BathroomsTotal.queryKey;
			break;
		case Filter.BedroomsTotal.label:
			searchKey = Filter.BedroomsTotal.queryKey;
			break;
		case Filter.DistanceToSea.label:
			searchKey = Filter.DistanceToSea.queryKey;
			break;
		case Filter.DistanceToCenter.label:
			searchKey = Filter.DistanceToCenter.queryKey;
			break;
		case Filter.DistanceToGolf.label:
			searchKey = Filter.DistanceToGolf.queryKey;
			break;
		case VermietungCategories.PropertyType.label:
			searchKey = PropertyTypeQueryKey;
			break;
		case Filter.LivingArea.label:
			searchKey = Filter.LivingArea.queryKey;
			break;
		case Filter.PlotSize.label:
			searchKey = Filter.PlotSize.queryKey;
			break;
	}

	return searchKey;
}

export function handleDropdownSelect(
	key: string,
	items: Item[],
	router: NextRouter,
	t: CustomTFunction<TranslationsKeys>
) {
	let searchKey = getQueryParamByLabel(key);

	let queryParams = router.query;
	if (
		!(items.length > 0) ||
		(items.filter((item) => item.name === FilterAnyLabels.BELIEBIG).length >
			0 &&
			router.query[searchKey]) ||
		(items.filter((item) => item.name === t(FilterAnyLabels.BELIEBIG)).length >
			0 &&
			router.query[searchKey])
	) {
		delete queryParams[searchKey];
	} else if (
		Object.values(Distances).find((dist) => dist.label === items[0].name)
	) {
		const dist = Object.values(Distances).find(
			(dist) => dist.label === items[0].name
		);
		queryParams = {...queryParams, [searchKey]: dist?.match_code.toString()};
	} else if (
		Object.values(NumberOfRooms).find((room) => room.label === items[0].name)
	) {
		const rooms = Object.values(NumberOfRooms).find(
			(room) => room.label === items[0].name
		);
		queryParams = {...queryParams, [searchKey]: rooms?.value};
	} else {
		const values = items
			.map((item) => {
				const parsedValue = item.name.match(/\d+/);
				return parsedValue && parsedValue.length > 0
					? parsedValue[0]
					: item.name;
			})
			.filter((name) => name != FilterAnyLabels.BELIEBIG);
		queryParams = {...queryParams, [searchKey]: values};
	}
	replaceRouterQueryParamsShallow(router, queryParams);
}

export function handleChecklistFilterChange(
	item: string | number,
	checked: boolean,
	router: NextRouter,
	returnQueryparamsDontReplaceRouter?: boolean
) {
	let searchKey = "";
	let searchVal = ["1"];

	const filter = Object.values(Filter).find((filter) => filter.label === item);
	if (filter) {
		searchKey = filter?.queryKey;
		if (filter.values) {
			searchVal = filter.values;
		}
	} else if (item === FilterAnyLabels.BELIEBIG) {
		let queryParams = router.query;
		delete queryParams[PropertyTypeQueryKey];
		replaceRouterQueryParamsShallow(router, queryParams);
		return;
	}

	const alreadySetValues = router.query[searchKey];
	let newSearchValue: string | string[];
	let queryParams = router.query;
	if (!checked) {
		delete queryParams[searchKey];
		if (
			Array.isArray(alreadySetValues) &&
			JSON.stringify(alreadySetValues) != JSON.stringify(searchVal)
		) {
			newSearchValue = alreadySetValues.filter(
				(val) => !searchVal.includes(val)
			);
			queryParams = {...queryParams, [searchKey]: newSearchValue};
		}
	} else {
		if (
			alreadySetValues &&
			JSON.stringify(alreadySetValues) != JSON.stringify(searchVal) &&
			Array.isArray(searchVal)
		) {
			const alreadySetValuesArray = enforceArray(alreadySetValues);
			newSearchValue = [...alreadySetValuesArray, ...searchVal];
		} else {
			newSearchValue = searchVal;
		}
		if (searchKey === Filter.SouthOrientation.queryKey && checked) {
			newSearchValue = newSearchValue.filter((v) =>
				filter?.values?.includes(v)
			);
		}
		const translatedChecked = checked === true ? newSearchValue : "0";
		queryParams = {...queryParams, [searchKey]: translatedChecked};
	}
	if (returnQueryparamsDontReplaceRouter) {
		return queryParams;
	} else {
		replaceRouterQueryParamsShallow(router, queryParams);
	}
}

export function extractInitialSelectedValues(
	queryKey: string,
	router: NextRouter
) {
	if (router.query[queryKey] instanceof Array) {
		return router.query[queryKey] as string[];
	} else if (router.query[queryKey]) {
		return [router.query[queryKey]] as string[];
	}
}

export const enforceArray = (obj: any) => {
	if (Array.isArray(obj)) return obj;
	return [obj];
};

export type sortingOption = {
	label: string;
	queryKey?: string;
	sortDirection?: string;
};

export function handleSort(
	selectedSortingOption: Item[],
	sortingOptions: sortingOption[],
	router: NextRouter
) {
	const selectedOptionName = selectedSortingOption[0]?.name;
	const selectedOption = sortingOptions.find(
		(o) => o.label === selectedOptionName
	);
	let queryParams = router.query;
	if (
		selectedOption &&
		selectedOption.queryKey &&
		selectedOption.sortDirection
	) {
		queryParams = {
			...queryParams,
			sort: selectedOption.queryKey + "+" + selectedOption.sortDirection
		};
	} else if (selectedOption) {
		delete queryParams.sort;
	}
	replaceRouterQueryParamsShallow(router, queryParams);
}
